import CryptoJS from 'crypto-js';
import qs from 'qs';
import { getCookie } from '@/utils/cookie';
import { encryptData, generateKey, importPublicKey } from '@/utils/encryption';
import { authRefresh, unauthenticate } from './services/users';
import { notification } from 'antd';
import { createElement } from 'react';
import { captureError, addBreadcrumb } from '../sentry';
import { addDebugApiLog } from '../debug/DebugContext';

export type FetcherConfig = Omit<RequestInit, 'body'> & {
	api: string;
	query?: unknown;
	handleAuth?: boolean;
	handleError?: boolean;
	body?:
		| string
		| {
				[key: string]: unknown;
		  };
};

export type FleetResponse<T = unknown> = {
	data: T;
	metadata: null;
	status: string;
};

export type FleetPaginated<T = unknown> = {
	currentPage: number;
	pageSize: number;
	totalCount: number;
	totalPages: number;
	items: T[];
};

export type FleetPagination = {
	pageNumber: number;
	pageSize: number;
};

export type FleetSearch = {
	by: string;
	value: string;
};

export type FleetGetListParams = {
	pagination: FleetPagination;
	search?: FleetSearch;
};

export type FleetQueryParams = {
	pagination?: FleetPagination;
	filter?: FleetSearch | object;
	search?: FleetSearch;
	myCustomer?: boolean;
	reviewStatus?: number;
	prospectStatus?: string;
};

export type FleetFile = {
	id: number | string;
	name: string;
	path?: string;
	format?: 'pdf' | 'jpg' | 'jpeg' | 'png';
	url?: string;
};

let pendingAccessToken: Promise<false> | false = false;

// Request deduplication map
const pendingRequests = new Map<string, Promise<unknown>>();

export default async function fetcher<T = unknown>({
	api,
	query,
	body,
	handleAuth = true,
	handleError = true,
	...config
}: FetcherConfig) {
	const startTime = Date.now();
	const accessToken = getCookie('FLTAT');
	const refreshToken = getCookie('FLTRT');

	// Create unique request key for deduplication
	const method = config.method || 'POST';
	const requestKey = `${method}:${api}:${JSON.stringify(query)}:${JSON.stringify(body)}`;

	// Check if similar request is already pending
	if (pendingRequests.has(requestKey)) {
		console.warn(
			`Duplicate request detected for ${requestKey}, waiting for existing request to complete`,
		);
		return pendingRequests.get(requestKey) as Promise<FleetResponse<T>>;
	}

	if (handleAuth && !accessToken && refreshToken) {
		if (pendingAccessToken instanceof Promise) {
			await pendingAccessToken;
		} else {
			let resolveAccessToken: ((value: false) => void) | undefined;
			pendingAccessToken = new Promise<false>(
				(resolve) => (resolveAccessToken = resolve),
			);

			await authRefresh(refreshToken);
			resolveAccessToken?.(false);
			pendingAccessToken = false;
		}
	}

	const baseURL = import.meta.env.VITE_FLEET_API_URL as string;
	const requestURL = baseURL.concat(
		api,
		qs.stringify(query, { addQueryPrefix: true }),
	);

	// Create and store the request promise for deduplication
	const requestPromise = (async () => {
		const payload = await generateFleetPayload(body);
		const response = await fetch(requestURL, {
			method: 'POST',
			...config,
			body: payload.body,
			headers: {
				...payload.headers,
				...config.headers,
			},
		});

		if (response.ok && response.status >= 200 && response.status <= 299) {
			const isJSON =
				response.headers.get('Content-Type')?.includes('application/json') ??
				false;

			let responseData: FleetResponse<T>;
			if (isJSON) {
				try {
					responseData = (await response.json()) as FleetResponse<T>;
				} catch {
					responseData = (await response.text()) as unknown as FleetResponse<T>;
				}
			} else {
				const decrypted = decryptFleetResponse(
					await response.text(),
					payload.secret,
				);

				try {
					responseData = JSON.parse(decrypted) as FleetResponse<T>;
				} catch {
					responseData = decrypted as unknown as FleetResponse<T>;
				}
			}

			// Log successful API call for debugging
			const duration = Date.now() - startTime;
			addDebugApiLog({
				endpoint: requestURL,
				method: config.method || 'POST',
				payload: body,
				response: responseData,
				status: response.status,
				duration,
				isError: false,
			});

			return responseData;
		}

		const errorAuth =
			(response.status === 401 && handleAuth) ||
			(response.status === 400 && response.url.endsWith('refresh-token'));

		if (errorAuth) {
			await unauthenticate({ logout: false });
			notification.destroy();
			notification.error({
				message: 'Session Expired',
				description: 'Silakan login kembali',
				duration: 3,
				onClose: () => {
					window.location.reload();
				},
			});
		}

		if (!errorAuth && handleError) {
			const message = await response.json();

			// Log API error for debugging if enabled
			const duration = Date.now() - startTime;
			addDebugApiLog({
				endpoint: requestURL,
				method: config.method || 'POST',
				payload: body,
				error: message,
				status: response.status,
				duration,
				isError: true,
			});

			// Capture critical API errors in Sentry
			if (response.status >= 500) {
				const error = new Error(
					`API Error: ${response.status} ${response.statusText}`,
				);
				captureError(error, {
					api_endpoint: api,
					response_status: response.status,
					error_id: message?.metadata?.debugInfo?.errorId,
					error_messages: message?.metadata?.debugInfo?.messages,
					request_body: body,
				});
			}

			// Add breadcrumb for debugging context
			addBreadcrumb(
				`API Error: ${api} - ${response.status}`,
				'api.error',
				response.status >= 500 ? 'error' : 'warning',
			);

			notification.error({
				duration: 20,
				message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
				description: createElement(
					'div',
					null,
					message?.metadata?.debugInfo?.messages &&
						message?.metadata?.debugInfo?.messages.length
						? createElement(
								'ul',
								null,
								message?.metadata?.debugInfo?.messages.map((msg: string) =>
									createElement('li', null, msg),
								),
							)
						: 'Ops, Something went wrong',
				),
			});
		}

		throw response;
	})();

	// Store the request promise for deduplication
	pendingRequests.set(requestKey, requestPromise);

	// Clean up the request from the map when it completes
	requestPromise
		.finally(() => {
			pendingRequests.delete(requestKey);
		})
		.catch(() => {
			// Ensure cleanup even on error
			pendingRequests.delete(requestKey);
		});

	return requestPromise;
}

fetcher.isError = (error: unknown): error is Response => {
	return error instanceof Response;
};

export const generateFleetPayload = async (payload?: FetcherConfig['body']) => {
	const { VITE_FLEET_PUBLIC_KEY, VITE_FLEET_INSIDER } = import.meta.env;
	const publicKey = await importPublicKey(VITE_FLEET_PUBLIC_KEY);
	const secret = generateKey();
	const apiKey = await encryptData(secret, publicKey);
	const headers: HeadersInit = {
		'MTF-API-KEY': apiKey,
	};

	const accessToken = getCookie('FLTAT');
	const mtfInsider = VITE_FLEET_INSIDER || localStorage.getItem('MTF-INSIDER');
	if (accessToken) headers['MTF-AUTH'] = accessToken;
	if (mtfInsider) headers['MTF-INSIDER'] = mtfInsider;

	let body: string | undefined;
	if (payload) {
		const jsonString = JSON.stringify(payload);
		body = mtfInsider ? jsonString : encryptFleetPayload(jsonString, secret);

		headers['Content-Type'] = mtfInsider ? 'application/json' : 'text/plain';
	}

	return {
		headers,
		secret,
		body,
	};
};

export const encryptFleetPayload = (payload: string, secret: string) => {
	const iv = import.meta.env.VITE_FLEET_IV;
	const encrypted = CryptoJS.AES.encrypt(
		payload,
		CryptoJS.enc.Utf8.parse(secret),
		{
			mode: CryptoJS.mode.CBC,
			iv: CryptoJS.enc.Utf8.parse(iv),
			padding: CryptoJS.pad.Pkcs7,
		},
	);

	return encrypted.toString();
};

export const decryptFleetResponse = (response: string, secret: string) => {
	const iv = import.meta.env.VITE_FLEET_IV;
	const decrypted = CryptoJS.AES.decrypt(
		response,
		CryptoJS.enc.Utf8.parse(secret),
		{
			mode: CryptoJS.mode.CBC,
			iv: CryptoJS.enc.Utf8.parse(iv),
			padding: CryptoJS.pad.Pkcs7,
		},
	);

	return decrypted.toString(CryptoJS.enc.Utf8);
};
